// BlogPostList.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../AuthContext";
import "./BlogPostList.css";

const BlogPostList = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const { token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/blog/posts`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setPosts(response.data);
        setError(null);
      } catch (err) {
        setError("Failed to fetch blog posts");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [token]);

  const handleStatusChange = async (postId, newStatus) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/blog/posts/${postId}/${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Refresh the posts list
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/blog/posts`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPosts(response.data);
    } catch (err) {
      setError(
        `Failed to update post status: ${
          err.response?.data?.message || err.message
        }`
      );
      console.error(err);
    }
  };

  const filteredPosts = posts.filter((post) => {
    // Apply status filter
    if (filter !== "all" && post.status !== filter) return false;

    // Apply search filter
    if (
      searchTerm &&
      !post.title.toLowerCase().includes(searchTerm.toLowerCase())
    )
      return false;

    return true;
  });

  if (loading) return <div className="loading">Loading posts...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="blog-post-list">
      <div className="blog-post-list-filters">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search posts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-container">
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">All Posts</option>
            <option value="draft">Drafts</option>
            <option value="pending_approval">Pending Approval</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </div>

      <table className="blog-post-table">
        <thead>
          <tr>
            <th>Title</th>
            <th>Author</th>
            <th>Category</th>
            <th>Status</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredPosts.length === 0 ? (
            <tr>
              <td colSpan="6" className="no-posts">
                No posts found
              </td>
            </tr>
          ) : (
            filteredPosts.map((post) => (
              <tr key={post._id}>
                <td>{post.title}</td>
                <td>
                  {post.author.firstName} {post.author.lastName}
                </td>
                <td>{post.category}</td>
                <td>
                  <span className={`status-badge ${post.status}`}>
                    {post.status.replace("_", " ")}
                  </span>
                </td>
                <td>{new Date(post.createdAt).toLocaleDateString()}</td>
                <td className="actions">
                  <button
                    className="view-btn"
                    onClick={() => navigate(`/blog/${post.slug}`)}
                  >
                    View
                  </button>
                  <button
                    className="edit-btn"
                    onClick={() => navigate(`/staff/blog/edit/${post.slug}`)}
                  >
                    Edit
                  </button>

                  {post.status === "draft" && (
                    <button
                      className="submit-btn"
                      onClick={() => handleStatusChange(post._id, "submit")}
                    >
                      Submit
                    </button>
                  )}

                  {post.status === "pending_approval" && (
                    <>
                      <button
                        className="approve-btn"
                        onClick={() => handleStatusChange(post._id, "approve")}
                      >
                        Approve
                      </button>
                      <button
                        className="reject-btn"
                        onClick={() =>
                          navigate(`/staff/blog/review/${post._id}`)
                        }
                      >
                        Review
                      </button>
                    </>
                  )}

                  {post.status === "approved" && (
                    <button
                      className="publish-btn"
                      onClick={() => handleStatusChange(post._id, "publish")}
                    >
                      Publish
                    </button>
                  )}

                  {post.status === "published" && (
                    <button
                      className="archive-btn"
                      onClick={() => handleStatusChange(post._id, "archive")}
                    >
                      Archive
                    </button>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default BlogPostList;
